import { gql } from '@apollo/client';
import { Schema, IncomeSchema, YearlyInfoSchema, YearlyInfoNextYearSchema } from './schema';
import { Gender, ResidenceStatus, HandicapType, FileType } from '@jbc-year-end-adj/types';
import { OTHER_FAMILIES_FRAGMENT } from './query';

export const ANSWER_DEPENDENTS = gql`
  mutation answerDependents($input: AnswerDependentsInput!) {
    answerDependents(input: $input) {
      questions {
        id
        status
      }
      profile {
        id
        ...OtherFamilies
      }
    }
  }
  ${OTHER_FAMILIES_FRAGMENT}
`;

export type QueryVariables = {
  input: {
    attributes: {
      dependents: OtherFamily[];
    };
  };
};

type OtherFamily = {
  id?: string;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  birthday: string;
  gender: Gender;
  relationOther: string;
  thisYear: YearlyInfo;
  income: Income;
  nextYear: YearlyInfoNextYear;
  dependentFrom?: string;
  dependentReason?: string;
  diedOn?: string;
};

type Income = {
  salaryIncome?: string;
  businessIncome?: string;
  businessExpense?: string;
  miscellaneousIncomeOfficalPension?: string;
  miscellaneousIncomeOther?: string;
  miscellaneousExpense?: string;
  dividendIncome?: string;
  dividendExpense?: string;
  realEstateIncome?: string;
  realEstateExpense?: string;
  retirementIncome?: string;
  lengthOfService?: string;
  retireForDisablity?: boolean;
  isOfficerRetirementAllowance?: boolean;
  otherIncome?: string;
  otherExpense?: string;
  otherExpenseSpecialDeduction?: string;
  otherEarings?: string;
};

type YearlyInfo = {
  dependentTaxLaw?: boolean;
  incomeAdjustmentDeduction?: boolean;
  residenceStatus?: ResidenceStatus;
  postcode0?: string;
  postcode1?: string;
  prefectureId?: string;
  city?: string;
  street?: string;
  building?: string;
  addressForeign?: string;
  isNonResident?: boolean;
  relatedToRelativesDocument?: File | FileType | null;
  relatedToRemittanceDocument?: File | FileType | null;
  isStudyAbroad?: boolean;
  provingStudyAbroadDocument?: File | FileType | null;
  remittance?: string;
  handicapType?: HandicapType;
  handicapDetail?: string;
  handicapImage?: File | FileType | null;
  earnings?: string;
};

type YearlyInfoNextYear = {
  dependentTaxLaw?: boolean;
  incomeAdjustmentDeduction?: boolean;
  residenceStatus?: ResidenceStatus;
  postcode0?: string;
  postcode1?: string;
  prefectureId?: string;
  city?: string;
  street?: string;
  building?: string;
  addressForeign?: string;
  isNonResident?: boolean;
  relatedToRelativesDocument?: File | FileType | null;
  relatedToRemittanceDocument?: File | FileType | null;
  isStudyAbroad?: boolean;
  provingStudyAbroadDocument?: File | FileType | null;
  remittance?: string;
  handicapType?: HandicapType;
  handicapDetail?: string;
  handicapImage?: File | FileType | null;
  income?: string;
  earnings?: string;
};
export const convertFormDataToQueryVariables = (data: Schema): QueryVariables => {
  const dependents = data.otherFamilies.map(otherFamily => {
    const thisYearDependentTaxLaw = otherFamily.thisYear?.dependentTaxLaw === 'true';
    const incomeAdjustmentDeduction = !thisYearDependentTaxLaw && otherFamily.thisYear?.incomeAdjustmentDeduction === 'true';
    const nextYearDependentTaxLaw = otherFamily.nextYear?.dependentTaxLaw === 'true';

    const otherFamilyAttributes: OtherFamily = {
      id: otherFamily.id,
      lastName: otherFamily.lastName,
      firstName: otherFamily.firstName,
      lastNameKana: otherFamily.lastNameKana,
      firstNameKana: otherFamily.firstNameKana,
      gender: otherFamily.gender,
      birthday: otherFamily.birthday,
      relationOther: otherFamily.relationOther,
      thisYear: {},
      income: {},
      nextYear: {}
    };

    if (thisYearDependentTaxLaw || incomeAdjustmentDeduction) {
      otherFamilyAttributes.thisYear = {
        dependentTaxLaw: thisYearDependentTaxLaw,
        incomeAdjustmentDeduction: incomeAdjustmentDeduction,
        handicapType: otherFamily.thisYear?.yearlyInfo.handicapType,
        handicapDetail: otherFamily.thisYear?.yearlyInfo.handicapDetail,
        handicapImage: otherFamily.thisYear?.yearlyInfo.handicapImage,
        ...convertYearlyInfoByResidenceStatus(otherFamily.thisYear?.yearlyInfo)
      };

      otherFamilyAttributes.income = convertIncome(otherFamily.hasIncomeDetail, otherFamily.thisYear?.yearlyInfo?.income);
    } else {
      otherFamilyAttributes.thisYear = {
        dependentTaxLaw: thisYearDependentTaxLaw,
        incomeAdjustmentDeduction: incomeAdjustmentDeduction
      };
    }

    if (nextYearDependentTaxLaw) {
      otherFamilyAttributes.nextYear = {
        dependentTaxLaw: nextYearDependentTaxLaw,
        handicapType: otherFamily.nextYear?.yearlyInfo.handicapType,
        handicapDetail: otherFamily.nextYear?.yearlyInfo.handicapDetail,
        handicapImage: otherFamily.nextYear?.yearlyInfo.handicapImage,
        ...convertYearlyInfoByResidenceStatus(otherFamily.nextYear?.yearlyInfo),
        income: otherFamily.nextYear?.yearlyInfo?.income || '0',
        earnings: otherFamily.nextYear?.yearlyInfo?.earnings || '0'
      };
    } else {
      otherFamilyAttributes.nextYear = {
        dependentTaxLaw: nextYearDependentTaxLaw
      };
    }

    if (thisYearDependentTaxLaw || nextYearDependentTaxLaw) {
      otherFamilyAttributes.dependentFrom = otherFamily.dependentFrom || undefined;
      otherFamilyAttributes.dependentReason = otherFamily.dependentReason;
      otherFamilyAttributes.diedOn = otherFamily.diedOn || undefined;
    }

    return otherFamilyAttributes;
  });

  return { input: { attributes: { dependents } } };
};

const convertIncome = (hasIncomeDetail: boolean, income?: IncomeSchema) => {
  if (!hasIncomeDetail) {
    return {
      salaryIncome: income?.salaryIncome || '0',
      businessIncome: '0',
      businessExpense: '0',
      miscellaneousIncomeOfficalPension: '0',
      miscellaneousIncomeOther: '0',
      miscellaneousExpense: '0',
      dividendIncome: '0',
      dividendExpense: '0',
      realEstateIncome: '0',
      realEstateExpense: '0',
      retirementIncome: '0',
      lengthOfService: '0',
      retireForDisablity: false,
      isOfficerRetirementAllowance: false,
      otherIncome: '0',
      otherExpense: '0',
      otherExpenseSpecialDeduction: '0',
      otherEarings: '0'
    };
  }

  return {
    salaryIncome: income?.salaryIncome || '0',
    businessIncome: income?.businessIncome || '0',
    businessExpense: income?.businessExpense || '0',
    miscellaneousIncomeOfficalPension: income?.miscellaneousIncomeOfficalPension || '0',
    miscellaneousIncomeOther: income?.miscellaneousIncomeOther || '0',
    miscellaneousExpense: income?.miscellaneousExpense || '0',
    dividendIncome: income?.dividendIncome || '0',
    dividendExpense: income?.dividendExpense || '0',
    realEstateIncome: income?.realEstateIncome || '0',
    realEstateExpense: income?.realEstateExpense || '0',
    retirementIncome: income?.retirementIncome || '0',
    lengthOfService: income?.lengthOfService || '0',
    retireForDisablity: income?.retireForDisablity,
    isOfficerRetirementAllowance: income?.isOfficerRetirementAllowance,
    otherIncome: income?.otherIncome || '0',
    otherExpense: income?.otherExpense || '0',
    otherExpenseSpecialDeduction: income?.otherExpenseSpecialDeduction || '0',
    otherEarings: income?.otherEarings || '0'
  };
};

const convertYearlyInfoByResidenceStatus = (yearlyInfo?: YearlyInfoSchema['yearlyInfo'] | YearlyInfoNextYearSchema['yearlyInfo']) => {
  switch (yearlyInfo?.residenceStatus) {
    case 'same_address':
      return {
        residenceStatus: yearlyInfo.residenceStatus
      };
    case 'different_address':
      return {
        residenceStatus: yearlyInfo.residenceStatus,
        postcode0: yearlyInfo.postcode0,
        postcode1: yearlyInfo.postcode1,
        prefectureId: yearlyInfo.prefectureId,
        city: yearlyInfo.city,
        street: yearlyInfo.street,
        building: yearlyInfo.building
      };
    case 'different_and_foreign_address':
      return {
        residenceStatus: yearlyInfo.residenceStatus,
        addressForeign: yearlyInfo.addressForeign,
        isNonResident: yearlyInfo.isNonResident,
        remittance: yearlyInfo.remittance || '0',
        relatedToRelativesDocument: yearlyInfo.relatedToRelativesDocument,
        relatedToRemittanceDocument: yearlyInfo.relatedToRemittanceDocument,
        isStudyAbroad: yearlyInfo.isStudyAbroad,
        provingStudyAbroadDocument: yearlyInfo.provingStudyAbroadDocument
      };
    default:
      return {};
  }
};
